// firebase/firebaseMessaging.js
import { messaging } from './firebase';
import { getToken, deleteToken, onMessage } from 'firebase/messaging';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';
// import { useDispatch } from 'react-redux';
import { baseAPI } from '../services/baseApi';
import { chatApi } from '../services/chat/chatAPIs';
import { remove_session } from 'utils/functions';
import { openMoveResultModal } from 'redux/slices/moveResultModalSlice';
import { customDayApis } from 'services/content/custom-days';

const getVapidKey = () =>
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BEIPzxZbZfD6TqRLAALo_gek5aWKnjGhgWew1HeqRxCrnKOnFdI4ZrmR2Zun4MIh6KBy_jyfYQv5fdhC9svSUVk';

// Request permission and get the token
// forceRefresh: when true, deletes cached token first (use when subscribe fails with invalid-registration-token)
export const requestFirebaseToken = async (forceRefresh = false) => {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied or dismissed.');
        return null;
      }
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      console.warn('Notification permission was previously denied.');
      return null;
    }

    const vapidKey = getVapidKey();

    if (forceRefresh) {
      try {
        await deleteToken(messaging);
      } catch (e) {
        // Ignore - token may not exist
      }
    }

    const currentToken = await getToken(messaging, { vapidKey });
    if (currentToken) {
      return currentToken;
    }
    console.error('No registration token available.');
  } catch (err) {
    console.error('Error retrieving token:', err);
  }
  return null;
};

// Subscribe to FCM topic with retry on invalid token (clears cached token and retries)
export const subscribeToTopic = async (connectToFirebase, token, topic) => {
  const result = await connectToFirebase({ token, topic }).unwrap();
  if (result?.failureCount > 0 && result?.errors?.[0]?.error?.code === 'messaging/invalid-registration-token') {
    const freshToken = await requestFirebaseToken(true);
    if (freshToken) {
      return connectToFirebase({ token: freshToken, topic }).unwrap();
    }
  }
  return result;
};

// Same scheme the clients list uses, so links open the client detail page.
const safeEncrypt = (value, key) => {
  try {
    return CryptoJS.AES.encrypt(String(value), key).toString();
  } catch (e) {
    return '';
  }
};

// Listen for messages when the app is in the foreground
export const listenForMessages = (dispatch, navigate) => {
  // const dispatch = useDispatch();
  const bellSound = new Audio('/notification.wav');

  onMessage(messaging, (payload) => {
    // Handle different types of messages

    switch (payload?.data?.notificationType || payload?.data?.type) {
      case 'CUSTOM_DAY_CHAT':
      case 'GENERIC_TRAINER_CHAT':
        // Invalidate chat-related queries to trigger refetch
        dispatch(baseAPI.util.invalidateTags(['CONVERSATION_LIST', 'MESSAGE_LIST']));

        // Show toast notification for new chat message
        toast.success(`${payload?.data?.title}`, {
          onClick: () => {
            // Optionally navigate to chat or focus on chat window
            window.location.href = '/chat';
          }
        });

        // Play notification sound
        bellSound.play().catch((error) => {
          console.error('Error playing the bell sound:', error);
        });
        break;
      case 'NUTRITION_CHANGE': {
        dispatch(baseAPI.util.invalidateTags(['NOTIFICATION_LIST']));
        const cid = payload?.data?.clientId || '';
        toast.success(payload?.data?.body || payload?.data?.title || 'A client modified their nutrition plan.', {
          onClick: () => {
            const enc = safeEncrypt(cid, '_.UVt{Wi@IBCnmt');
            if (enc) navigate(`/user-details?user-id=${encodeURIComponent(enc)}`);
            else navigate('/clients');
          }
        });
        bellSound.play().catch(() => {});
        break;
      }
      case 'trainingRequestTransfer':
        dispatch(baseAPI.util.invalidateTags(['TRAINING_REQUESTS_LIST']));
        dispatch(baseAPI.util.invalidateTags(['NOTIFICATION_LIST']));
        // Invalidate chat-related queries to trigger refetch
        dispatch(baseAPI.util.invalidateTags(['CONVERSATION_LIST', 'MESSAGE_LIST']));
      case 'customWorkoutComplete':
        dispatch(baseAPI.util.invalidateTags(['NOTIFICATION_LIST']));
        if (payload?.data) {
          const d = payload.data;
          const clientId = d?.client?._id || d?.client || '';
          const getDay = customDayApis.endpoints.getCustomDayById.initiate({ id: d?.dayId });
          const promise = dispatch(getDay);
          promise
            .unwrap()
            .then((day) => {
              const params = new URLSearchParams({
                client: day?.user?._id || clientId || '',
                programId: day?.program?._id || '',
                weekId: day?.week?._id || '',
                trainingRequestId: day?.trainingRequestId || '',
                name: day?.user?.fullName || day?.user?.userName || '',
                'program-name': day?.program?.title || '',
                'week-name': day?.week?.title || '',
                'client-status': 'active'
              });
              toast.success(`${d?.title || 'Notification'}`, {
                onClick: () => navigate(`/custom-days?${params.toString()}`)
              });
            })
            .catch(() => {
              toast.success(`${d?.title || 'Notification'}`, {
                onClick: () => navigate(`/custom-days?client=${clientId}`)
              });
            });
        }
        bellSound.play().catch((error) => {
          console.error('Error playing the bell sound:', error);
        });
        break;
      case 'trainingRequest':
        // Invalidate chat-related queries to trigger refetch
        dispatch(baseAPI.util.invalidateTags(['TRAINING_REQUESTS_LIST']));
        dispatch(baseAPI.util.invalidateTags(['NOTIFICATION_LIST']));
        // Invalidate chat-related queries to trigger refetch
        dispatch(baseAPI.util.invalidateTags(['CONVERSATION_LIST', 'MESSAGE_LIST']));
        const requestId = payload?.data?.requestId ? `?request-id=${payload?.data?.requestId}` : '';
        if (payload?.data?.requestId && payload?.data?.title == 'Assessment completed') {
          const requestId = payload?.data?.requestId;
          const client = payload?.data?.client;
          const clientName = payload?.data?.clientName;
          toast.success(payload?.data?.title, {
            onClick: () => navigate(`/custom-moves?client=${client}&name=${clientName}&requestId=${requestId}`)
          });
        } else {
          toast.success(payload?.data?.title, {
            onClick: () => navigate(`/training-requests${requestId}`)
          });
        }
        if (payload?.data?.title)
          // Play notification sound
          bellSound.play().catch((error) => {
            console.error('Error playing the bell sound:', error);
          });
        break;
      case 'moveResult':
        dispatch(baseAPI.util.invalidateTags(['NOTIFICATION_LIST']));
        if (payload?.data?.moveResultId) {
          toast.success(`${payload?.data?.title}`, {
            onClick: () => dispatch(openMoveResultModal(payload?.data?.moveResultId))
          });
        } else if (payload?.data?.title) {
          toast.success(`${payload?.data?.title}`);
        }
        bellSound.play().catch((error) => {
          console.error('Error playing the bell sound:', error);
        });
        break;
      case 'userLogout':
        remove_session();
        window.location.pathname = '/login';
        break;
      case 'PERMISSIONS_UPDATED':
        // Notify usePermissions to refresh - admin's role permissions were updated
        try {
          const permissions = payload?.data?.permissions ? JSON.parse(payload.data.permissions) : [];
          window.dispatchEvent(
            new CustomEvent('permissionsUpdated', {
              detail: { roleName: payload?.data?.roleName, permissions }
            })
          );
          toast.success(payload?.notification?.title || 'Your permissions have been updated.');
        } catch (e) {
          console.error('Error parsing PERMISSIONS_UPDATED:', e);
        }
        break;
      default:
        // Handle other notification types
        dispatch(baseAPI.util.invalidateTags(['NOTIFICATION_LIST']));
        toast.success(`${payload?.data?.title}`);
        // Play notification sound
        bellSound.play().catch((error) => {
          console.error('Error playing the bell sound:', error);
        });
        break;
    }
  });
};

// Function to handle real-time chat updates
export const setupChatListener = (chatRoomId, dispatch) => {
  // This would typically be implemented on the backend
  // to push real-time updates via WebSocket or Firebase
  const unsubscribe = () => {
    // Cleanup function for the listener
  };

  return unsubscribe;
};
